/** Device UI data type
 * @description Type definition for Device UI (dui) data structure
 * Instructions: Each DUI field should have its own type definition
 */
import type { VirtualDeviceTemplate } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_types';

// Whitelisted DUI keys
export enum DeviceUIField {
	FURNITURE = 'furniture',
	QUICK_CONTROL_TIMER = 'quick_control_timer',
	DEVICE_SIDE_PANEL_VIEW = 'device_side_panel_view',
	VC_TEMPLATE = 'vc_template',
}

// DUI Data for 'furniture' — new fraction-based format (v2).
// Positions are stored as fractions (0..1) of the canvas size so they survive
// canvas resize. On load, old-format items are migrated automatically.
export type DeviceUIFurnitureItem = {
	id: string; // unique instance ID, e.g. "furn-1234abc"
	src: string; // SVG filename, e.g. "chair1.svg"
	name: string;
	xFrac: number; // top-left x as fraction of canvas width
	yFrac: number; // top-left y as fraction of canvas height
	wFrac: number; // width as fraction of canvas width
	hFrac: number; // height as fraction of canvas height
	rotation: number; // degrees
};

// Legacy v1 format stored by older versions — kept for migration only.
export type DeviceUIFurnitureLegacyItem = {
	id: number;
	type: string;
	name: string;
	img: string;
	width: number;
	height: number;
	refX: number;
	refY: number;
	rotation: number;
};

export type DeviceUIFurniture = Array<DeviceUIFurnitureItem | DeviceUIFurnitureLegacyItem>;

//DUI Data for 'quick_control_timer'
export type DeviceUIQuickControlTimer = {
	hr: string;
	min: string;
	sec: string;
	icon?: string;
};

// View types
export type ViewCategory =
	| 'relay'
	| 'fan'
	| 'light'
	| 'sensor'
	| 'pill_sensor'
	| 'emeter'
	| 'power_meter'
	| 'roller'
	| 'inputs_reader'
	| 'remote_control'
	| 'bluetooth_sensor'
	| 'peripheral_device';
export type ViewVariant = 'basic' | 'advanced' | 'compact';

export type DeviceUIDeviceSidePanelView = {
	category: ViewCategory;
	variant: ViewVariant;
};

export type DeviceUI = Partial<{
	[DeviceUIField.FURNITURE]?: DeviceUIFurniture;
	[DeviceUIField.QUICK_CONTROL_TIMER]?: DeviceUIQuickControlTimer;
	[DeviceUIField.DEVICE_SIDE_PANEL_VIEW]?: DeviceUIDeviceSidePanelView;
	[DeviceUIField.VC_TEMPLATE]?: VirtualDeviceTemplate;
}>;

export interface DeviceUIList {
	[device_id: string]: DeviceUI;
}
